import { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { unwrapResult } from "@reduxjs/toolkit";

import CustomInput from "../../../uiKit/Input/CustomInput";
import Title from "../../../uiKit/Title/Title";
import CustomButton from "../../../uiKit/Button/CustomButton";
import CustomCheckbox from "../../../uiKit/CustomCheckbox/customCheckbox";
import { gray800 } from "../../../uiKit/assets/colors/colors";
import { Trash } from "../../../uiKit/assets/svg/icons";
import CustomSelect from "../../../uiKit/CustomSelect/CustomSelect";
import { DONATION_FEE, AUTO_DONATE_DESCRIPTION } from "../../../helpers/constants";
import { onlyNumbers } from "../../../helpers/validation";

import {
  addAutoDonate,
  changeIsAnonymous,
  getSubscriptions,
} from "../../../redux/state/paymentSlice";
import { getCommunities } from "../../../redux/state/communitySlice";
import { setAutoDonateOpen } from "../../../redux/state/donationSlice";

const PredefinedOptions = [100, 54, 36, 18];

const AutoDonateContent = () => {
  const dispatch = useDispatch();

  const isAnonymous = useSelector((state) => state.payment.isAnonymous, shallowEqual);
  const savedCard = useSelector((state) => state.payment.savedCard, shallowEqual);
  const memberCommunities = useSelector((state) => state.community.memberCommunities, shallowEqual);

  const [amount, setAmount] = useState(null);
  const [amountError, setAmountError] = useState("");
  const [coverFees, setCoverFees] = useState(true);

  const [communities, setCommunities] = useState([]);
  const [communityId, setCommunityId] = useState("all");

  const donationAmountHandler = (e) => {
    const value = e.target.value;
    if (onlyNumbers(value)) {
      if (value === "0") {
        setAmountError("Insufficient amount");
        setAmount(null);
      } else {
        setAmountError("");
        setAmount(parseInt(value));
      }
    } else {
      setAmountError("Required");
      setAmount(null);
    }
  };

  const coverFeeConfirmHandler = () => {
    setCoverFees((prev) => !prev);
  };

  const predefinedOptionsHandler = (amount) => {
    setAmount((prev) => prev + amount);
  };

  const anonymousHandler = (event) => {
    const value = event.target.checked;
    dispatch(changeIsAnonymous(value));
  };

  const subscribeHandler = () => {
    if (!savedCard?.id) return;
    const communityIds = communities.map((community) => community.id);
    const paymentMethodId = savedCard.id;
    const data = {
      paymentMethodId,
      amount,
      isAnonymous,
      coverFees,
      allCommunities: !communityIds.length,
      communityIds,
    };

    dispatch(addAutoDonate(data))
      .then(unwrapResult)
      .then(() => {
        cancelHandler();
        toast.info("Success");
        dispatch(getSubscriptions({ page: 1, pageSize: 3 }));
      })
      .catch(({ title }) => toast.error(title));
  };

  useEffect(() => {
    if (!!memberCommunities?.length) return;
    dispatch(getCommunities());
  }, [dispatch, memberCommunities]);

  const communityHandler = (event) => {
    const id = event.target.value;
    if (id === "all") {
      setCommunities([]);
      setCommunityId("all");
      return;
    }
    const community = memberCommunities?.find((x) => x.id === id);
    if (!community || communities.some((x) => x.id === id)) return;
    setCommunityId(id);
    setCommunities((prev) => [...prev, community]);
  };

  const removeCommunityHandler = (id) => {
    setCommunities((prev) => prev.filter((x) => x.id !== id));
    setCommunityId("");
  };

  const cancelHandler = () => {
    dispatch(setAutoDonateOpen(false));
  };

  const confirmedCommunities = memberCommunities?.filter((x) => x.isConfirmed);
  const data = !!confirmedCommunities
    ? [{ id: "all", name: "All Communities" }, ...confirmedCommunities]
    : [];

  return (
    <div className="autoDonate">
      <Title text="Auto-donate" lineHeight={40} fontSize={32} />
      <Title
        text={AUTO_DONATE_DESCRIPTION}
        fontWeight={400}
        fontSize={14}
        color={gray800}
        overflowText={false}
      />
      <div className="amountInputWrapper">
        <CustomSelect
          label={"Community Name"}
          data={data}
          onChange={communityHandler}
          value={communityId}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "16px",
            marginBottom: !!communities?.length ? "16px" : 0,
          }}
        >
          {communities.map((community) => (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                <Title text={community.name} color={gray800} />
                <Title
                  text={`Active Campaigns: ${community.numberOfActiveCampaigns}`}
                  fontSize={12}
                  fontWeight={400}
                />
                {!!community.location && (
                  <Title text={`Location: ${community.location}`} fontSize={12} fontWeight={400} />
                )}
              </div>
              <div onClick={() => removeCommunityHandler(community.id)}>
                <Trash />
              </div>
            </div>
          ))}
        </div>
        <CustomInput
          label={"Donation Amount"}
          onChange={donationAmountHandler}
          inputMode={"numeric"}
          value={amount}
          error={!!amountError}
          errorMessage={amountError}
          baseStyles={{ marginBottom: !amountError ? "8px" : "28px" }}
        />
        <div className="definedOptions">
          {PredefinedOptions.map((x) => (
            <div>
              <Title text={`$ ${x}`} fontSize={12} onClick={() => predefinedOptionsHandler(x)} />
            </div>
          ))}
        </div>
      </div>

      <CustomCheckbox label={DONATION_FEE} checked={coverFees} onChange={coverFeeConfirmHandler} />
      <CustomCheckbox
        checked={isAnonymous}
        onChange={anonymousHandler}
        label="Donate Anonymously"
      />
      <CustomButton title={"Subscribe"} onClick={subscribeHandler} />
      <CustomButton isWhite title={"Cancel"} onClick={cancelHandler} />
    </div>
  );
};

export default AutoDonateContent;
